<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { computed, ref } from "vue";
import SettingsComponent from "@/views/Calculators/PersonalDates/components/SettingsComponent.vue";
import {
  FavorableDateSelectionRequest,
  FavorableMonthReport,
  MonthDayReport,
} from "@/types/calculators";
import { fetchMonthDates } from "@/api/favorableDates";
import BestDaysByCategoryComponent from "@/views/Calculators/PersonalDates/components/monthReport/BestDaysByCategoryComponent.vue";
import TopDaysComponent from "@/views/Calculators/PersonalDates/components/monthReport/TopDaysComponent.vue";
import MonthTableComponent from "@/views/Calculators/PersonalDates/components/monthReport/MonthTableComponent.vue";
import DayReportPreviewComponent from "@/views/Calculators/PersonalDates/components/monthReport/DayReportPreviewComponent.vue";
import DayDetailReportComponent from "@/views/Calculators/PersonalDates/components/dayReport/DayDetailReportComponent.vue";

const { t } = useI18n();
const showSettings = ref(true);
const showDetailsView = ref(false);
const monthReport = ref<FavorableMonthReport | null>(null);
const selectedCard = ref<MonthDayReport | null>(null);
const lastRequest = ref<FavorableDateSelectionRequest | null>(null);
const isCalendarView = ref<boolean>(true);

const selectedDateString = computed(() => {
  if (!selectedCard.value) {
    return "";
  }
  const [, month, day] = selectedCard.value.date.split("-");

  return `(${day}.${month})`;
});

async function getMonthData(request: FavorableDateSelectionRequest) {
  lastRequest.value = request;
  monthReport.value = await fetchMonthDates(request);
}

async function handleSelectedCard(
  dayReport: MonthDayReport,
  isForceOpen = false
) {
  if (
    isForceOpen ||
    (selectedCard.value && selectedCard.value.date === dayReport.date)
  ) {
    showDetailsView.value = true;
  }
  selectedCard.value = dayReport;
}

function handleBackToCalendar() {
  showDetailsView.value = false;
}

function handleNextDay() {
  const context = getContextInfo();
  if (!context) return;

  const { days, dayIndex } = context;

  if (dayIndex === days.length - 1) return;

  selectedCard.value = days[dayIndex + 1];
}

function handlePrevDay() {
  const context = getContextInfo();
  if (!context) return;

  const { days, dayIndex } = context;

  if (dayIndex === 0) return;

  selectedCard.value = days[dayIndex - 1];
}

function handleSelectDay(selectedDay: MonthDayReport) {
  selectedCard.value = selectedDay;
  showDetailsView.value = true;
}

function handleToggleDetailView() {
  if (selectedCard.value) {
    showDetailsView.value = true;
  }
}

function getContextInfo() {
  const days = monthReport.value?.days;
  const currentCard = selectedCard.value;

  if (!days || !currentCard) return null;

  const dayIndex = days.findIndex((day) => day.date === currentCard.date);
  if (dayIndex === -1) return null;

  return { days, dayIndex };
}
</script>

<template>
  <div class="overflow-y-auto no-scrollbar h-full w-full">
    <div
      class="pt-2 xl:pt-[100px] px-1 xl:px-8 pb-8 flex flex-col gap-4 text-left"
    >
      <div class="flex justify-between max-md:flex-col">
        <div class="text-2xl sm:text-3xl font-bold text-brown">
          {{ t("calculators.personal-date.title") }}
        </div>
        <div
          class="flex items-center bg-white p-1 rounded-xl border border-divider shadow-sm self-start sm:self-auto w-full sm:w-auto overflow-x-auto"
        >
          <!-- SETTINGS BUTTON -->
          <button
            class="font-semibold px-3 py-2 rounded-lg text-xs transition-all flex items-center justify-center gap-1.5 shrink-0"
            :class="
              showSettings
                ? 'bg-beige text-active'
                : 'text-alternative-gray hover:text-brown'
            "
            @click="showSettings = !showSettings"
          >
            <span>{{ t("calculators.personal-date.settings") }}</span>
            <span class="text-[10px]" :class="{ 'rotate-180': !showSettings }">
              ▲
            </span>
          </button>
          <div class="w-px h-4 bg-divider mx-1 shrink-0"></div>
          <!-- FULL CALENDAR BUTTON -->
          <button
            class="flex-1 sm:flex-initial px-3.5 py-2 rounded-lg text-xs transition-all flex items-center justify-center gap-1.5 shrink-0"
            :class="
              showDetailsView
                ? 'text-alternative-gray hover:text-brown'
                : 'bg-brown text-white shadow-sm'
            "
            @click="showDetailsView = false"
          >
            <span>{{ t("calculators.personal-date.calendar-view") }}</span>
          </button>
          <!-- DAY REPORT BUTTON -->
          <button
            class="font-semibold flex-1 sm:flex-initial px-3.5 py-2 rounded-lg text-xs transition-all flex items-center justify-center gap-1.5 shrink-0"
            :class="
              showDetailsView
                ? 'bg-brown text-white shadow-sm'
                : 'text-alternative-gray hover:text-brown'
            "
            @click="handleToggleDetailView"
          >
            <span>
              {{
                t("calculators.personal-date.day-report", {
                  day: selectedDateString,
                })
              }}
            </span>
          </button>
        </div>
      </div>
      <SettingsComponent
        :showFullCard="showSettings"
        @submitForm="getMonthData"
      />
      <template v-if="monthReport !== null && !showDetailsView">
        <BestDaysByCategoryComponent
          :startDay="monthReport.meta.start_date"
          :endDay="monthReport.meta.end_date"
          :bestDaysCount="monthReport.range_summary.counts"
          :dayStem="monthReport.user_natal_summary.day_master.stem"
          :yearPillar="
            monthReport.range_summary.top_recommended_days[0].pillars.year
          "
          :isCalendarView="isCalendarView"
          @showAsCalendar="(asCalendar: boolean) => (isCalendarView = asCalendar)"
        />
        <TopDaysComponent
          :topDays="monthReport.range_summary.top_recommended_days"
          @selectDay="handleSelectDay"
        />
        <MonthTableComponent
          :bestDaysByCategory="monthReport.range_summary.best_days_by_category"
          :days="monthReport.days"
          :selectedCard="selectedCard"
          :isCalendarView="isCalendarView"
          @selectCard="handleSelectedCard"
        />
        <DayReportPreviewComponent
          v-if="selectedCard"
          :selectedCard="selectedCard"
          @openFullReport="showDetailsView = true"
        />
      </template>
      <DayDetailReportComponent
        v-if="selectedCard !== null && showDetailsView"
        :lastRequest="lastRequest"
        :selectedCard="selectedCard"
        @backToCalendar="handleBackToCalendar"
        @nextDay="handleNextDay"
        @prevDay="handlePrevDay"
      />
    </div>
  </div>
</template>
